import React, { useMemo, useState } from "react";
import Container from "../../../components/Container";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

export default function Faq() {
  const { t } = useTranslation();
  const cats = t("qna.categories", { returnObjects: true }) || [];
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q.trim()) return cats;
    const needle = q.toLowerCase();
    return cats
      .map((c) => ({
        ...c,
        items: (c.items || []).filter(
          (it) =>
            String(it.q).toLowerCase().includes(needle) ||
            String(it.a).toLowerCase().includes(needle)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [cats, q]);

  return (
    <div className="text-blue-900">
      <Hero />
      <ProgramOverview /> {/* 🔹 브로슈어 스타일 섹션 */}
      <section className="py-10">
        <Container>
          {filtered.length === 0 ? (
            <p className="mt-6 text-center text-sm text-slate-500">
              {t("qna.noResult")}
            </p>
          ) : (
            <div className="mt-8 space-y-10">
              {filtered.map((cat) => (
                <div key={cat.id}>
                  <h2 className="text-xl font-bold md:text-2xl">{cat.title}</h2>
                  <div className="mt-4 divide-y rounded-xl border bg-white shadow-sm">
                    {cat.items.map((item, idx) => (
                      <Accordion key={idx} q={item.q} a={item.a} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(120%_60%_at_0%_0%,#e6f0ff,transparent_60%),radial-gradient(120%_60%_at_100%_100%,#eef2ff,transparent_60%)]" />
      <Container className="py-14 md:py-20">
        <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-[11px] text-blue-700">
          {t("qna.badge")}
        </span>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight md:text-5xl">
          {t("qna.title")}
        </h1>
        <p className="mt-2 max-w-prose text-slate-600">{t("qna.desc")}</p>
      </Container>
    </section>
  );
}

/**
 * 브로슈어 “취업 과정 프로그램” 영역
 */
function ProgramOverview() {
  const { t } = useTranslation();

  return (
    <section className="border-b bg-slate-50/60">
      <Container className="py-10 md:py-14">
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
          {t("qna.program.sectionTitle")}
        </h2>

        <div className="mt-6 flex flex-col gap-8 md:flex-row md:items-start">
          {/* 왼쪽: 이미지 + 설명 */}
          <div className="md:w-[35%]">
            <div className="flex items-center justify-center">
              <div className="h-52 w-52 overflow-hidden rounded-full border-4 border-white shadow-lg md:h-60 md:w-60">
                <img
                  src="/images/services/cruise/internship.jpg"
                  alt={t("qna.program.imageAlt")}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            <div className="mt-5 rounded-xl border-l-4 border-blue-500 bg-white/80 p-4 text-sm leading-6 text-slate-800 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {t("qna.program.left.badge")}
              </p>
              <p className="mt-1 font-semibold text-blue-900">
                {t("qna.program.left.title")}
              </p>
              <p className="mt-1 text-[13px] leading-6">
                {t("qna.program.left.desc1")}
              </p>
              <p className="mt-2 text-[13px] font-semibold text-blue-700">
                {t("qna.program.left.desc2")}
              </p>
            </div>
          </div>

          {/* 오른쪽: 타임라인 스타일 프로그램 카드들 */}
          <div className="flex-1 md:w-[65%]">
            <div className="relative">
              {/* 세로 점선 라인 */}
              <div className="absolute left-2 top-3 bottom-3 hidden w-px bg-[repeating-linear-gradient(to_bottom,#e2e8f0_0,#e2e8f0_4px,transparent_4px,transparent_8px)] md:block" />

              <div className="space-y-4">
                <ProgramItem
                  colorBar="bg-sky-400"
                  label={t("qna.program.item1.label")}
                  highlightClass="text-sky-600"
                >
                  {t("qna.program.item1.desc")}
                </ProgramItem>

                <ProgramItem
                  colorBar="bg-amber-400"
                  label={t("qna.program.item2.label")}
                  highlightClass="text-amber-600"
                >
                  {t("qna.program.item2.desc")}
                </ProgramItem>

                <ProgramItem
                  colorBar="bg-rose-300"
                  label={t("qna.program.item3.label")}
                  highlightClass="text-rose-500"
                >
                  {t("qna.program.item3.desc")}
                </ProgramItem>

                <ProgramItem
                  colorBar="bg-orange-400"
                  label={t("qna.program.item4.label")}
                  highlightClass="text-orange-500"
                >
                  {t("qna.program.item4.desc")}
                </ProgramItem>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function ProgramItem({ label, children, colorBar, highlightClass }) {
  return (
    <div className="relative pl-0 md:pl-10">
      {/* 점 위치 (md 이상에서만) */}
      <div className="absolute -left-[5px] top-5 hidden h-3 w-3 rounded-full border-2 border-white bg-blue-400 shadow-md md:block" />

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <div className={`h-1 ${colorBar}`} />
        <div className="p-4">
          <p className={`text-sm font-bold ${highlightClass}`}>{label}</p>
          <p className="mt-1 text-xs leading-6 text-slate-700 md:text-[13px]">
            {children}
          </p>
        </div>
      </div>
    </div>
  );
}

function Accordion({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="group">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-medium text-slate-900">{q}</span>
        <ChevronDown
          className={`mt-1 h-4 w-4 shrink-0 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`grid overflow-hidden px-4 pb-4 text-sm text-slate-700 transition-[grid-template-rows] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0">
          <div className="rounded-lg bg-slate-50/70 p-4">{a}</div>
        </div>
      </div>
    </div>
  );
}
